import { apiClient } from './client';

// Alineado con aws-serverless/src/models/capital.model.ts (F4).

export const CAPITAL_SOURCE_TYPES = ['INVESTOR', 'BANK', 'FIRA', 'OWN'] as const;
export type CapitalSourceType = (typeof CAPITAL_SOURCE_TYPES)[number];

export const CAPITAL_SOURCE_STATUSES = ['PLANNED', 'ACTIVE', 'CLOSED'] as const;
export type CapitalSourceStatus = (typeof CAPITAL_SOURCE_STATUSES)[number];

export interface CapitalSummary {
  projectId: string;
  /** null cuando las fuentes están en varias monedas. */
  currency: string | null;
  currencies: string[];
  byCurrency: Record<string, { totalCommitted: number; totalDrawn: number; pendingToDraw: number }>;
  totalCommitted: number | null;
  totalDrawn: number | null;
  pendingToDraw: number | null;
  bySource: Array<{
    sourceId: string;
    name: string;
    type: CapitalSourceType;
    currency?: string;
    committed: number;
    drawn: number;
    pending: number;
  }>;
}

export const DRAWDOWN_STATUSES = ['PLANNED', 'RECEIVED', 'CANCELLED'] as const;
export type DrawdownStatus = (typeof DRAWDOWN_STATUSES)[number];

export interface Drawdown {
  drawdownId: string;
  tenantId: string;
  sourceId: string;
  projectId: string;
  plannedAmount: number;
  plannedDate: string;
  actualAmount?: number;
  actualDate?: string;
  currency: string;
  status: DrawdownStatus;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export const FUND_REQUEST_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export type FundRequestStatus = (typeof FUND_REQUEST_STATUSES)[number];

export interface FundRequest {
  requestId: string;
  tenantId: string;
  projectId: string;
  amount: number;
  currency: string;
  category: string;
  justification: string;
  attachments?: string[];
  requestedBy: string;
  approvalId?: string;
  status: FundRequestStatus;
  resolvedAt?: string;
  resolvedBy?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CashflowRow {
  month: string;
  inflow: number;
  outflow: number;
  net: number;
  balance: number;
  isDeficit: boolean;
}

export interface Cashflow {
  projectId: string;
  fromMonth: string;
  months: number;
  rows: CashflowRow[];
  deficitMonths: string[];
}

export interface CurrencyInvestmentMetrics {
  totalInvested: number;
  totalCollected: number;
  roi: number | null;
  monthlyIrr: number | null;
  annualIrr: number | null;
  paybackMonths: number | null;
}

/** Con varias monedas los escalares vienen en null y vale `byCurrency`. */
export interface InvestmentMetrics {
  projectId: string;
  totalInvested: number | null;
  totalCollected: number | null;
  roi: number | null;
  monthlyIrr: number | null;
  annualIrr: number | null;
  paybackMonths: number | null;
  currencies?: string[];
  byCurrency?: Record<string, CurrencyInvestmentMetrics>;
  capital: CapitalSummary;
}

export const fetchCapitalSummary = async (projectId: string): Promise<CapitalSummary> =>
  (await apiClient.get(`/api/projects/${projectId}/capital`)).data;

export const fetchDrawdowns = async (projectId: string): Promise<Drawdown[]> =>
  (await apiClient.get(`/api/projects/${projectId}/drawdowns`)).data.items;

export const fetchFundRequests = async (projectId: string): Promise<FundRequest[]> =>
  (await apiClient.get(`/api/projects/${projectId}/fund-requests`)).data.items;

export const fetchCashflow = async (projectId: string, months?: number): Promise<Cashflow> =>
  (await apiClient.get(`/api/projects/${projectId}/cashflow`, { params: months ? { months } : undefined })).data;

export const fetchInvestmentMetrics = async (projectId: string): Promise<InvestmentMetrics> =>
  (await apiClient.get(`/api/projects/${projectId}/investment`)).data;
